import { Button, Dialog, Message } from '@alifd/next';
import { Box } from '@mui/material';
import { DatePicker, Form, Radio } from 'antd';
import moment from 'moment';
import React, { useCallback, useState } from 'react';

import downloadBlobFile from '@/utils/downloadBlobFile';

import type { DownloadBlobFn } from './utils';
import {
  getIncomeStatementFile,
  getMonthlyBillsFile,
  getUnpaidBillsFile,
} from './utils';

const { useForm, Item } = Form;

const downloadTypes: {
  [key: string]: {
    label: string;
    getBlobFile: DownloadBlobFn<any>;
    getFileName: (time: moment.Moment) => string;
  };
} = {
  incomeStatement: {
    label: '收入明細表',
    getBlobFile: getIncomeStatementFile,
    getFileName: (time) => `${time.format('YYYYMM')}_收入明細表.xlsx`,
  },
  monthlyBills: {
    label: '紙本月帳單',
    getBlobFile: getMonthlyBillsFile,
    getFileName: (time) => `${time.format('YYYYMM')}_月帳單.pdf`,
  },
  unpaidBills: {
    label: '管理費欠繳統計表',
    getBlobFile: getUnpaidBillsFile,
    getFileName: (time) => `${time.format('YYYYMMDD')}_管理費欠繳統計表.xlsx`,
  },
};

const DownloadDialog: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const [downloadType, setDownloadType] = useState<
    'incomeStatement' | 'monthlyBills' | 'unpaidBills'
  >('incomeStatement');
  const [downloadForm] = useForm();

  const handleFinish = useCallback(
    async (values) => {
      const { getBlobFile, getFileName } = downloadTypes[downloadType];
      try {
        Message.loading({
          duration: 0,
          content: '下載中...',
        });
        const blob = await getBlobFile(values);
        console.log(blob);
        switch (downloadType) {
          case 'incomeStatement':
            downloadBlobFile(blob, getFileName(values.timeStart));
            break;
          case 'monthlyBills':
            downloadBlobFile(blob, getFileName(values.timeStart));
            break;
          case 'unpaidBills':
            downloadBlobFile(blob, getFileName(moment()));
            break;
        }
        Message.success('下載成功');
      } catch (error) {
        if (downloadType === 'monthlyBills') {
          return Message.error(await error.response.data.text());
        }
        return Message.error('下載失敗');
      }
    },
    [downloadType]
  );

  const handleCancel = useCallback(() => {
    setVisible(false);
    setDownloadType('incomeStatement');
    downloadForm.resetFields();
  }, [downloadForm]);

  return (
    <>
      <Button onClick={() => setVisible(true)}>下載表單</Button>
      <Dialog
        title="下載表單"
        visible={visible}
        onOk={() => downloadForm.submit()}
        onCancel={handleCancel}
        onClose={handleCancel}
        okProps={{ children: '下載' }}
        cancelProps={{ children: '取消' }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '20px',
            height: '200px',
            width: '400px',
          }}
        >
          <Radio.Group
            value={downloadType}
            onChange={(e) => setDownloadType(e.target.value)}
          >
            {Object.entries(downloadTypes).map(([name, type]) => (
              <Radio key={name} value={name}>
                {type.label}
              </Radio>
            ))}
          </Radio.Group>
          <Form
            form={downloadForm}
            onFinish={handleFinish}
            labelCol={{ span: 7 }}
            labelAlign="left"
            wrapperCol={{ span: 17 }}
          >
            {downloadType === 'incomeStatement' && (
              <>
                <Item
                  label="選擇月份"
                  name="timeStart"
                  rules={[{ required: true, message: '請選擇月份' }]}
                >
                  <DatePicker picker="month" />
                </Item>
              </>
            )}
            {downloadType === 'monthlyBills' && (
              <>
                <Item
                  label="選擇月份"
                  name="timeStart"
                  rules={[{ required: true, message: '請選擇月份' }]}
                >
                  <DatePicker picker="month" />
                </Item>
                <Item label="過濾已付款帳單" name="isFilterPaid">
                  <Radio.Group>
                    <Radio value="true">是</Radio>
                    <Radio value="false">否</Radio>
                  </Radio.Group>
                </Item>
              </>
            )}
          </Form>
        </Box>
      </Dialog>
    </>
  );
};

export default DownloadDialog;
